import { useState } from "react";

import type { SupportTicketDetail } from "../../shared/support-schemas";
import { Button } from "../components/ui/button";
import { describeActivity, formatTimestamp } from "./support-format";

const FIRST_SHOWN = 12;

/**
 * Everything that happened to the ticket, newest first (ADR-0089): who wrote, replied, or changed
 * a field, what the AI did, and which emails went out. The conversation shows only the messages.
 */
export function SupportTicketActivity({
  ticket,
  names,
}: {
  ticket: SupportTicketDetail;
  names: ReadonlyMap<string, string>;
}) {
  const [showAll, setShowAll] = useState(false);
  const entries = ticket.events
    .flatMap((event) => {
      const text = describeActivity(event, names);
      return text === null ? [] : [{ id: event.id, text, at: event.createdAt }];
    })
    .reverse();
  const shown = showAll ? entries : entries.slice(0, FIRST_SHOWN);
  return (
    <section
      aria-labelledby="support-activity-title"
      className="flex flex-col gap-3 rounded-xl bg-card px-5 py-5 shadow-card"
    >
      <h2 id="support-activity-title" className="text-sm font-semibold text-muted-foreground">
        Activity
      </h2>
      {entries.length === 0 ? (
        <p className="text-sm text-muted-foreground">No activity yet.</p>
      ) : (
        <ol aria-label="Activity" className="flex flex-col gap-3">
          {shown.map((entry) => (
            <li key={entry.id} className="flex flex-col gap-0.5">
              <span className="text-sm">{entry.text}</span>
              <time className="text-xs text-muted-foreground">{formatTimestamp(entry.at)}</time>
            </li>
          ))}
        </ol>
      )}
      {entries.length > FIRST_SHOWN ? (
        <Button
          type="button"
          variant="ghost"
          className="self-start"
          onClick={() => {
            setShowAll(!showAll);
          }}
        >
          {showAll ? "Show less" : `Show all ${String(entries.length)}`}
        </Button>
      ) : null}
    </section>
  );
}
